import React, { useState, useEffect, useRef, useCallback } from 'react';
import './WhatsAppWeb.css';
import { getApiBaseUrl,getApiUrl   } from './config/api';

const WhatsAppWeb = ({ selectedInstance, onBackToDashboard }) => {
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileCaption, setFileCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatNumber, setNewChatNumber] = useState('');
  const [adminStatuses, setAdminStatuses] = useState({}); // Store admin status for each contact
  const [searchQuery, setSearchQuery] = useState(''); // Search state
  const [showImageModal, setShowImageModal] = useState(false); // Image modal state
  const [selectedImageUrl, setSelectedImageUrl] = useState(''); // Selected image URL
  const [imageZoom, setImageZoom] = useState(1); // Image zoom level
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // Mobile detection with initial check
  const [showSidebar, setShowSidebar] = useState(true); // Sidebar visibility for mobile
  const [isUserScrolling, setIsUserScrolling] = useState(false); // Track if user is manually scrolling
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true); // Control auto-scroll behavior
  const [newMessageCount, setNewMessageCount] = useState(0); // Count of new messages while scrolling up
  const [isDragOver, setIsDragOver] = useState(false); // Drag and drop state
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const messagesContainerRef = useRef(null); // Reference to messages container for scroll detection
  const lastMessageCountRef = useRef(0); // Track previous message count
  const chatAreaRef = useRef(null); // Reference to chat area for drag and drop

  // Initialize mobile state on component mount
  useEffect(() => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (mobile) {
      // On mobile, always start with sidebar visible (contacts list)
      setShowSidebar(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to initialize mobile state

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        if (selectedChat) {
          setShowSidebar(false); // Hide sidebar when chat is selected on mobile
        } else {
          setShowSidebar(true); // Show sidebar when no chat is selected on mobile
        }
      } else {
        setShowSidebar(true); // Always show sidebar on desktop
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [selectedChat]);

  // Handle chat selection for mobile
  const handleChatSelect = (contact) => {
    console.log('Selecting chat:', contact.chat_id);
    setSelectedChat(contact.chat_id);
    fetchMessages(contact.chat_id);
    // Mark messages as read when chat is selected
    markMessagesAsRead(contact.chat_id);
    
    if (isMobile) {
      setShowSidebar(false); // Hide sidebar on mobile when chat is selected
    }
  };

  // Handle back to contacts on mobile
  const handleBackToContacts = () => {
    if (isMobile) {
      setSelectedChat(null);
      setShowSidebar(true); // Show sidebar when going back to contacts
    }
  };

  const checkStatus = useCallback(async () => {
    if (!selectedInstance) {
      setIsConnected(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setIsConnected(data.instance.status === 'connected');
      } else {
        setIsConnected(false);
      }
    } catch (error) {
      console.error('Status check failed:', error);
      setIsConnected(false);
    }
  }, [selectedInstance]);

  const fetchAdminStatuses = useCallback(async (contactsList) => {
    if (!selectedInstance || !contactsList.length) return;
    
    const token = localStorage.getItem('auth_token');
    const statusPromises = contactsList.map(async (contact) => {
      try {
        const response = await fetch(
          getApiBaseUrl(`/instances/${selectedInstance.id}/contact-admin-status?chat_id=${encodeURIComponent(contact.chat_id)}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        return {
          chatId: contact.chat_id,
          isAdminMode: data.success ? data.is_admin_mode : false,
        };
      } catch (error) {
        console.error('Failed to fetch admin status for', contact.chat_id, error);
        return {
          chatId: contact.chat_id,
          isAdminMode: false,
        };
      }
    });

    try {
      const results = await Promise.all(statusPromises);
      const statusMap = {};
      results.forEach(result => {
        statusMap[result.chatId] = result.isAdminMode;
      });
      setAdminStatuses(statusMap);
      console.log('Admin statuses updated:', statusMap);
    } catch (error) {
      console.error('Failed to fetch admin statuses:', error);
    }
  }, [selectedInstance]);

  const fetchContacts = useCallback(async () => {
    if (!selectedInstance) return;
    
    console.log('Fetching contacts for instanceId:', selectedInstance.id);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}/contacts`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      console.log('Contacts response:', data);
      if (data.success) {
        console.log('Setting contacts:', data.contacts || []);
        setContacts(data.contacts || []);
        // Fetch admin status for all contacts
        fetchAdminStatuses(data.contacts || []);
      } else {
        console.error('Fetch contacts error:', data.error);
      }
    } catch (error) {
      console.error('Fetch contacts failed:', error);
    }
  }, [selectedInstance, fetchAdminStatuses]);

  const fetchMessages = useCallback(async (chatId) => {
    if (!selectedInstance) return;
    
    console.log('Fetching messages for chatId:', chatId, 'instanceId:', selectedInstance.id);
    try {
      const token = localStorage.getItem('auth_token');
      const url = getApiBaseUrl(`/instances/${selectedInstance.id}/messages?chat_id=${encodeURIComponent(chatId)}`);
      console.log('Fetching messages from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      console.log('Messages response:', data);
      
      if (data.success) {
        console.log('Setting messages:', data.messages || []);
        setMessages(data.messages || []);
      } else {
        console.error('Fetch messages error:', data.error);
      }
    } catch (error) {
      console.error('Fetch messages failed:', error);
    }
  }, [selectedInstance]);

  useEffect(() => {
    console.log('WhatsAppWeb received selectedInstance:', selectedInstance);
    if (selectedInstance) {
      console.log('Checking status and fetching contacts...');
      checkStatus();
      fetchContacts();
    }
    const interval = setInterval(() => {
      if (selectedChat && selectedInstance) {
        console.log('Auto-fetching messages for chat:', selectedChat);
        fetchMessages(selectedChat);
      }
      if (selectedInstance) {
        fetchContacts(); // This will also refresh admin statuses
      }
    }, 2000); // Reduced interval to 2 seconds for faster admin status updates
    return () => clearInterval(interval);
  }, [selectedChat, selectedInstance, checkStatus, fetchContacts, fetchMessages]);

  // Add keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        if (showImageModal) {
          setShowImageModal(false);
        } else if (searchQuery) {
          setSearchQuery('');
          searchInputRef.current?.blur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery, showImageModal]);

  useEffect(() => {
    // Only auto-scroll if user is not manually scrolling and should auto-scroll
    if (shouldAutoScroll && !isUserScrolling) {
      scrollToBottom();
      setNewMessageCount(0); // Reset new message count when auto-scrolling
    } else if (!shouldAutoScroll && messages.length > lastMessageCountRef.current) {
      // User is scrolling up and new messages arrived
      const newMessages = messages.length - lastMessageCountRef.current;
      setNewMessageCount(prev => prev + newMessages);
    }
    
    // Update last message count
    lastMessageCountRef.current = messages.length;
  }, [messages, shouldAutoScroll, isUserScrolling]);

  // Add scroll detection to messages container
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      
      if (isAtBottom) {
        // User scrolled to bottom, enable auto-scroll
        setShouldAutoScroll(true);
        setIsUserScrolling(false);
        setNewMessageCount(0); // Reset new message count when user scrolls to bottom
      } else {
        // User is scrolling up, disable auto-scroll
        setShouldAutoScroll(false);
        setIsUserScrolling(true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedChat]); // Re-attach when chat changes

  // Reset scroll behavior when chat changes
  useEffect(() => {
    if (selectedChat) {
      setShouldAutoScroll(true);
      setIsUserScrolling(false);
      setNewMessageCount(0); // Reset new message count for new chat
      lastMessageCountRef.current = 0; // Reset message count tracking
      // Scroll to bottom when first opening a chat
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [selectedChat]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide drag overlay if leaving the chat area entirely
    if (!chatAreaRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0]; // Take the first file
      
      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        alert('File size must be less than 100MB');
        return;
      }
      
      setSelectedFile(file);
      setShowFileModal(true);
    }
  };

  // Paste handler for screenshots
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Create a more descriptive filename for screenshots
          const now = new Date();
          const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
          const renamedFile = new File([file], `screenshot-${timestamp}.png`, { type: file.type });
          
          setSelectedFile(renamedFile);
          setShowFileModal(true);
        }
        break;
      }
    }
  };

  // Add drag and drop event listeners
  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea || !selectedChat) return;

    chatArea.addEventListener('dragenter', handleDragEnter);
    chatArea.addEventListener('dragleave', handleDragLeave);
    chatArea.addEventListener('dragover', handleDragOver);
    chatArea.addEventListener('drop', handleDrop);

    return () => {
      chatArea.removeEventListener('dragenter', handleDragEnter);
      chatArea.removeEventListener('dragleave', handleDragLeave);
      chatArea.removeEventListener('dragover', handleDragOver);
      chatArea.removeEventListener('drop', handleDrop);
    };
  }, [selectedChat]);

  // Add paste event listener
  useEffect(() => {
    if (!selectedChat) return;

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [selectedChat]);

  const markMessagesAsRead = async (chatId) => {
    if (!selectedInstance || !chatId) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}/mark-read`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          chat_id: chatId,
        }),
      });

      const data = await response.json();
      if (data.success) {
        console.log('Messages marked as read for chat:', chatId);
        // Refresh contacts to update unread count
        setTimeout(() => fetchContacts(), 200);
      } else {
        console.error('Failed to mark messages as read:', data.error);
      }
    } catch (error) {
      console.error('Mark as read failed:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || !selectedInstance) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}/send`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          to: selectedChat,
          message: newMessage,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        // Refresh messages to show sent message
        setTimeout(() => fetchMessages(selectedChat), 500);
      } else {
        alert('Failed to send: ' + data.error);
      }
    } catch (error) {
      console.error('Send failed:', error);
      alert('Send failed: ' + error.message);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowFileModal(true);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile || !selectedChat || !selectedInstance) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('to', selectedChat);
    formData.append('caption', fileCaption);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}/media`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setShowFileModal(false);
        setSelectedFile(null);
        setFileCaption('');
        // Refresh messages to show uploaded file
        setTimeout(() => fetchMessages(selectedChat), 500);
      } else {
        alert('Failed to send file: ' + data.error);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to send file: ' + error.message);
    }
    setUploading(false);
  };

  const handleDownload = async (mediaUrl, fileName) => {
    try {
      console.log('Downloading file:', mediaUrl, fileName);
      
      // Add CORS headers and proper authentication if needed
      const token = localStorage.getItem('auth_token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch the file as blob to avoid navigation
      const response = await fetch(mediaUrl, {
        method: 'GET',
        headers: headers,
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('File downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      
      // Fallback: try opening in new tab
      console.log('Trying fallback method...');
      try {
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = fileName || 'download';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        alert('Download failed. Please try again or contact support.');
      }
    }
  };

  const handleImageClick = (imageUrl) => {
    // Open image in modal instead of new tab
    // Ensure the URL is properly formatted
    let fullImageUrl = imageUrl;
    if (imageUrl && !imageUrl.startsWith('http')) {
      fullImageUrl = getApiUrl(`${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`);
    }
    setSelectedImageUrl(fullImageUrl);
    setImageZoom(1); // Reset zoom when opening modal
    setShowImageModal(true);
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleZoomReset = () => {
    setImageZoom(1);
  };

  const handleImageWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      // Scroll up - zoom in
      setImageZoom(prev => Math.min(prev + 0.1, 3));
    } else {
      // Scroll down - zoom out
      setImageZoom(prev => Math.max(prev - 0.1, 0.5));
    }
  };

  const handleMediaError = (e, mediaType) => {
    console.error(`${mediaType} failed to load:`, e.target.src);
    e.target.style.display = 'none';
    
    // Show error message
    const errorDiv = e.target.parentNode.querySelector(`.${mediaType.toLowerCase()}-error`);
    if (errorDiv) {
      errorDiv.style.display = 'block';
    }
    
    // Hide loading indicator
    const loadingDiv = e.target.parentNode.querySelector('.media-loading');
    if (loadingDiv) {
      loadingDiv.style.display = 'none';
    }
  };

  const handleMediaLoadStart = (e) => {
    // Add loading indicator
    const loadingDiv = e.target.parentNode.querySelector('.media-loading');
    if (loadingDiv) {
      loadingDiv.style.display = 'block';
    }
  };

  const handleMediaLoadedData = (e) => {
    // Remove loading indicator
    const loadingDiv = e.target.parentNode.querySelector('.media-loading');
    if (loadingDiv) {
      loadingDiv.style.display = 'none';
    }
  };

  const getFullMediaUrl = (mediaUrl) => {
    if (!mediaUrl) return '';
    if (mediaUrl.startsWith('http')) return mediaUrl;
    return getApiUrl(`/${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const startNewChat = () => {
    if (!newChatNumber.trim()) return;
    
    // Format nomor telepon (tambah @s.whatsapp.net jika belum ada)
    let formattedNumber = newChatNumber.trim();
    if (!formattedNumber.includes('@')) {
      // Jika nomor dimulai dengan 0, ganti dengan 62
      if (formattedNumber.startsWith('0')) {
        formattedNumber = '62' + formattedNumber.substring(1);
      }
      // Jika nomor tidak dimulai dengan +, tambah 62 jika belum ada
      if (!formattedNumber.startsWith('62') && !formattedNumber.startsWith('+')) {
        formattedNumber = '62' + formattedNumber;
      }
      formattedNumber = formattedNumber + '@s.whatsapp.net';
    }
    
    setSelectedChat(formattedNumber);
    setShowNewChatModal(false);
    setNewChatNumber('');
    fetchMessages(formattedNumber);
  };

  // Filter contacts based on search query
  const filteredContacts = contacts.filter(contact => {
    if (!searchQuery.trim()) return true;
    
    const searchLower = searchQuery.toLowerCase();
    const displayName = (contact.display_name || '').toLowerCase();
    const chatId = contact.chat_id.toLowerCase();
    const lastMessage = (contact.last_message || '').toLowerCase();
    
    return displayName.includes(searchLower) || 
           chatId.includes(searchLower) || 
           lastMessage.includes(searchLower);
  });

  // Function to highlight search query in text
  const highlightText = (text, query) => {
    if (!query.trim() || !text) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="highlight">{part}</span>
      ) : part
    );
  };

  const renderMessage = (message) => {
    const isFromMe = message.is_from_me || message.status === 'sent';
    
    return (
      <div key={message.id} className={`message ${isFromMe ? 'sent' : 'received'}`}>
        <div className="message-content">
          {message.message_type === 'image' && (
            <div className="media-message">
              {message.media_url ? (
                <div className="image-container">
                  <img 
                    src={message.media_url} 
                    alt="Shared media"
                    className="message-image clickable-image"
                    onClick={() => handleImageClick(message.media_url)}
                    style={{ cursor: 'pointer' }}
                    title="Click to view full size"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="media-placeholder" style={{display: 'none'}}>
                    📷 Image: {message.media_name || 'Unknown'}
                    {message.media_size && (
                      <span className="file-size"> ({(message.media_size / 1024).toFixed(1)} KB)</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="media-placeholder">
                  📷 Image: {message.media_name || 'Unknown'}
                  {message.media_size && (
                    <span className="file-size"> ({(message.media_size / 1024).toFixed(1)} KB)</span>
                  )}
                </div>
              )}
              {message.content && <p className="media-caption">{message.content}</p>}
            </div>
          )}
          {message.message_type === 'document' && (
            <div className="media-message">
              <div className="document-message">
                <div className="document-info">
                  <span className="document-icon">📄</span>
                  <div className="document-details">
                    <div className="document-name">{message.media_name || 'Document'}</div>
                    {message.media_size && (
                      <div className="document-size">{(message.media_size / 1024).toFixed(1)} KB</div>
                    )}
                  </div>
                  {message.media_url && (
                    <button 
                      onClick={() => handleDownload(message.media_url, message.media_name)}
                      className="download-btn"
                      title="Download file"
                    >
                      ⬇️
                    </button>
                  )}
                </div>
              </div>
              {message.content && <p className="media-caption">{message.content}</p>}
            </div>
          )}
          {message.message_type === 'audio' && (
            <div className="media-message">
              {message.media_url ? (
                <div className="audio-container">
                  <div className="media-loading" style={{display: 'none', textAlign: 'center', padding: '10px'}}>
                    Loading audio...
                  </div>
                  <audio 
                    controls 
                    className="message-audio"
                    preload="metadata"
                    onLoadStart={handleMediaLoadStart}
                    onLoadedData={handleMediaLoadedData}
                    onError={(e) => handleMediaError(e, 'Audio')}
                  >
                    <source src={getFullMediaUrl(message.media_url)} type="audio/mpeg" />
                    <source src={getFullMediaUrl(message.media_url)} type="audio/wav" />
                    <source src={getFullMediaUrl(message.media_url)} type="audio/ogg" />
                    Your browser does not support the audio element.
                  </audio>
                  <div className="audio-error" style={{display: 'none', textAlign: 'center', padding: '10px', color: '#ef4444'}}>
                    Unable to play audio file
                  </div>
                  <div className="audio-info">
                    <span className="audio-icon">🎵</span>
                    <div className="audio-details">
                      <div className="audio-name">{message.media_name || 'Audio message'}</div>
                      {message.media_size && (
                        <div className="audio-size">{(message.media_size / 1024).toFixed(1)} KB</div>
                      )}
                    </div>
                    <button 
                      onClick={() => handleDownload(message.media_url, message.media_name || 'audio.mp3')}
                      className="download-btn"
                      title="Download audio"
                    >
                      ⬇️
                    </button>
                  </div>
                </div>
              ) : (
                <div className="audio-message">
                  🎵 Audio message
                  {message.media_name && <span> - {message.media_name}</span>}
                </div>
              )}
              {message.content && <p className="media-caption">{message.content}</p>}
            </div>
          )}
          {message.message_type === 'video' && (
            <div className="media-message">
              {message.media_url ? (
                <div className="video-container">
                  <div className="media-loading" style={{display: 'none', textAlign: 'center', padding: '10px'}}>
                    Loading video...
                  </div>
                  <video 
                    controls 
                    className="message-video"
                    preload="metadata"
                    poster={message.thumbnail_url}
                    onLoadStart={handleMediaLoadStart}
                    onLoadedData={handleMediaLoadedData}
                    onError={(e) => handleMediaError(e, 'Video')}
                  >
                    <source src={getFullMediaUrl(message.media_url)} type="video/mp4" />
                    <source src={getFullMediaUrl(message.media_url)} type="video/webm" />
                    <source src={getFullMediaUrl(message.media_url)} type="video/ogg" />
                    Your browser does not support the video element.
                  </video>
                  <div className="video-error" style={{display: 'none', textAlign: 'center', padding: '10px', color: '#ef4444'}}>
                    Unable to play video file
                  </div>
                  <div className="video-info">
                    <span className="video-icon">🎬</span>
                    <div className="video-details">
                      <div className="video-name">{message.media_name || 'Video'}</div>
                      {message.media_size && (
                        <div className="video-size">{(message.media_size / (1024 * 1024)).toFixed(1)} MB</div>
                      )}
                    </div>
                    <button 
                      onClick={() => handleDownload(message.media_url, message.media_name || 'video.mp4')}
                      className="download-btn"
                      title="Download video"
                    >
                      ⬇️
                    </button>
                  </div>
                </div>
              ) : (
                <div className="video-message">
                  🎬 Video
                  {message.media_name && <span> - {message.media_name}</span>}
                </div>
              )}
              {message.content && <p className="media-caption">{message.content}</p>}
            </div>
          )}
          {message.message_type === 'text' && (
            <p className="message-text">{message.content}</p>
          )}
          <span className="message-time">
            {formatTime(message.timestamp)}
            {isFromMe && (
              <span className="message-status">
                {message.status === 'sent' && ' ✓'}
                {message.status === 'delivered' && ' ✓✓'}
                {message.status === 'read' && ' ✓✓'}
              </span>
            )}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="whatsapp-web">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <button 
              onClick={onBackToDashboard || (() => window.history.back())} 
              className="back-to-dashboard-btn"
              title="Back to Dashboard"
            >
              ← Back to Dashboard
            </button>
            {/* <div className="header-title">
              WhatsApp Chat - {selectedInstance?.name || 'Unknown Device'}
            </div> */}
          </div>
          <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '● Connected' : '● Disconnected'}
          </div>
        </div>
      </div>

      <div className={`main-container ${isMobile && selectedChat ? 'chat-selected' : ''}`}>
        {/* Sidebar */}
        <div className={`sidebar ${!showSidebar && isMobile ? 'hidden' : ''}`}>
          <div className="sidebar-header">
            <h3>Chats</h3>
            <div className="header-actions">
              <button onClick={() => setShowNewChatModal(true)} className="new-chat-btn" title="New Chat">
                ✚
              </button>
              <button onClick={fetchContacts} className="refresh-btn" title="Refresh">🔄</button>
            </div>
          </div>
          
          {/* Search Input */}
          <div className="search-container">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts... (Ctrl+K)"
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <div className="contacts-list">
            {filteredContacts.length === 0 ? (
              <div className="no-contacts">
                {searchQuery ? (
                  <div className="no-search-results">
                    <p>No contacts found for "{searchQuery}"</p>
                    <button 
                      onClick={() => setSearchQuery('')} 
                      className="clear-search-btn"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <p>No contacts available</p>
                )}
              </div>
            ) : (
              filteredContacts.map((contact) => {
              const isAdminMode = adminStatuses[contact.chat_id] || false;
              console.log(`Contact ${contact.chat_id}: adminMode=${isAdminMode}, adminStatuses=`, adminStatuses);
              return (
                <div
                  key={contact.chat_id}
                  className={`contact-item ${selectedChat === contact.chat_id ? 'active' : ''} ${isAdminMode ? 'admin-mode' : ''}`}
                  onClick={() => handleChatSelect(contact)}
                >
                  <div className="contact-avatar">
                    {contact.display_name ? contact.display_name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div className="contact-info">
                    <div className="contact-name">
                      {highlightText(contact.display_name || contact.chat_id.split('@')[0], searchQuery)}
                      {isAdminMode && <span className="admin-badge">👤</span>}
                    </div>
                    <div className="contact-last-message">
                      {contact.last_message && contact.last_message.length > 30
                        ? highlightText(contact.last_message.substring(0, 30) + '...', searchQuery)
                        : highlightText(contact.last_message || 'No messages', searchQuery)}
                    </div>
                  </div>
                  <div className="contact-meta">
                    <div className="contact-time">
                      {contact.last_message_time ? formatTime(contact.last_message_time) : ''}
                    </div>
                    {contact.message_count > 0 && (
                      <div className="message-count">{contact.message_count}</div>
                    )}
                  </div>
                </div>
              );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`chat-area ${isMobile && !selectedChat ? 'hidden-mobile' : ''}`} ref={chatAreaRef}>
          {selectedChat ? (
            <>
        {/* Drag and Drop Overlay */}
        {isDragOver && (
          <div className="drag-drop-overlay">
            <div className="drag-drop-content">
              <div className="drag-drop-icon">�</div>
              <h3>Drop your files here</h3>
              <p>Supports images, videos, documents and more</p>
            </div>
          </div>
        )}              <div className="chat-header">
                {isMobile && (
                  <button 
                    className="mobile-back-btn"
                    onClick={handleBackToContacts}
                    aria-label="Back to contacts"
                  >
                    ←
                  </button>
                )}
                <div className="chat-contact-info">
                  <div className="chat-avatar">
                    {contacts.find(c => c.chat_id === selectedChat)?.display_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="chat-name">
                      {contacts.find(c => c.chat_id === selectedChat)?.display_name || selectedChat.split('@')[0]}
                    </div>
                    <div className="chat-status">
                      {selectedChat.includes('@g.us') ? 'Group' : 'Contact'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="messages-container" ref={messagesContainerRef}>
                {console.log('Rendering messages. Messages state:', messages)}
                {messages.length === 0 ? (
                  <div style={{padding: '20px', textAlign: 'center', color: '#999'}}>
                    {selectedChat ? 'No messages found' : 'Select a chat to view messages'}
                  </div>
                ) : (
                  messages.map(renderMessage)
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to bottom button - show when user is scrolling up */}
              {!shouldAutoScroll && (
                <div className="scroll-to-bottom">
                  <button 
                    onClick={() => {
                      setShouldAutoScroll(true);
                      setIsUserScrolling(false);
                      setNewMessageCount(0);
                      scrollToBottom();
                    }}
                    className="scroll-to-bottom-btn"
                    title="Go to latest messages"
                  >
                    ↓ {newMessageCount > 0 ? `${newMessageCount} new message${newMessageCount > 1 ? 's' : ''}` : 'New messages'}
                  </button>
                </div>
              )}

              <div className="message-input-container">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-input"
                />
                <button
                  className="attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  📎
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  className="message-input"
                />
                <button onClick={sendMessage} className="send-btn">
                  ➤
                </button>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-content">
                <h3>WhatsApp Web </h3>
                <p>Select a chat to start messaging</p>
                <div className="feature-list">
                  <p>✓ Send and receive messages</p>
                  <p>✓ Share files and images</p>
                  <p>✓ Real-time updates</p>
                  <p>✓ Message history</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* File Upload Modal */}
      {showFileModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Send File</h3>
              <button onClick={() => setShowFileModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-content">
              {selectedFile && (
                <div className="file-preview">
                  <div className="file-info">
                    <span className="file-icon">📄</span>
                    <div>
                      <div className="file-name">{selectedFile.name}</div>
                      <div className="file-size">{(selectedFile.size / 1024).toFixed(1)} KB</div>
                    </div>
                  </div>
                  {selectedFile.type.startsWith('image/') && (
                    <img 
                      src={URL.createObjectURL(selectedFile)} 
                      alt="Preview" 
                      className="image-preview"
                    />
                  )}
                </div>
              )}
              <textarea
                value={fileCaption}
                onChange={(e) => setFileCaption(e.target.value)}
                placeholder="Add a caption (optional)..."
                className="caption-input"
                rows="3"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowFileModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button 
                onClick={uploadFile} 
                disabled={uploading}
                className="btn-send"
              >
                {uploading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Start New Chat</h3>
              <button onClick={() => setShowNewChatModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-content">
              <div className="new-chat-form">
                <label htmlFor="phone-input">Enter Phone Number:</label>
                <input
                  id="phone-input"
                  type="text"
                  value={newChatNumber}
                  onChange={(e) => setNewChatNumber(e.target.value)}
                  placeholder="e.g., 628123456789 or 08123456789"
                  className="phone-input"
                  onKeyPress={(e) => e.key === 'Enter' && startNewChat()}
                />
                <div className="phone-help">
                  <small>
                    • Use format: 628123456789 (without +)<br/>
                    • Or use: 08123456789 (will be converted to 62)<br/>
                    • International format is also supported
                  </small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowNewChatModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button 
                onClick={startNewChat} 
                disabled={!newChatNumber.trim()}
                className="btn-send"
              >
                Start Chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <div className="image-modal-controls">
                <button 
                  onClick={handleZoomOut} 
                  className="zoom-btn"
                  title="Zoom Out"
                  disabled={imageZoom <= 0.5}
                >
                  🔍-
                </button>
                <button 
                  onClick={handleZoomReset} 
                  className="zoom-btn"
                  title="Reset Zoom"
                >
                  1:1
                </button>
                <button 
                  onClick={handleZoomIn} 
                  className="zoom-btn"
                  title="Zoom In"
                  disabled={imageZoom >= 3}
                >
                  🔍+
                </button>
              </div>
              <div className="image-modal-actions">
                <button 
                  onClick={() => handleDownload(selectedImageUrl, 'image.jpg')} 
                  className="image-modal-download"
                  title="Download"
                >
                  ⬇️
                </button>
                <button 
                  onClick={() => setShowImageModal(false)} 
                  className="image-modal-close"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="image-modal-content">
              <img 
                src={selectedImageUrl} 
                alt="Full size view"
                className="modal-image"
                style={{ transform: `scale(${imageZoom})` }}
                onWheel={handleImageWheel}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="image-error" style={{display: 'none'}}>
                <p>Unable to load image</p>
                <button onClick={() => setShowImageModal(false)}>Close</button>
              </div>
            </div>
            <div className="image-modal-footer">
              <p>Zoom: {Math.round(imageZoom * 100)}% • Click outside or press ESC to close</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppWeb;
